import React, { useEffect, useState } from "react";
import AnimatedCursor from "react-animated-cursor";
import { useLocation } from "react-router-dom";

function parseRgb(color) {
  if (!color || color === "transparent") return null;

  const match = color.match(
    /rgba?\(\s*([\d.]+)\s*[, ]\s*([\d.]+)\s*[, ]\s*([\d.]+)(?:\s*[,/]\s*([\d.]+%?))?\s*\)/i
  );
  if (!match) return null;

  const alphaRaw = match[4];
  let alpha = 1;
  if (alphaRaw !== undefined) {
    alpha = alphaRaw.endsWith("%")
      ? parseFloat(alphaRaw) / 100
      : parseFloat(alphaRaw);
  }

  return {
    r: Number(match[1]),
    g: Number(match[2]),
    b: Number(match[3]),
    a: alpha,
  };
}

function isDarkBackground(color) {
  const parsed = parseRgb(color);
  if (!parsed || parsed.a < 0.2) return null;

  const r = parsed.r * parsed.a + 255 * (1 - parsed.a);
  const g = parsed.g * parsed.a + 255 * (1 - parsed.a);
  const b = parsed.b * parsed.a + 255 * (1 - parsed.a);
  const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  return luminance < 0.35;
}

function isOverDarkSurface(x, y) {
  const stack = document.elementsFromPoint(x, y);

  for (const node of stack) {
    if (!(node instanceof Element)) continue;
    if (node.tagName === "IFRAME" || node.tagName === "HTML") continue;

    const dark = isDarkBackground(
      window.getComputedStyle(node).backgroundColor
    );
    if (dark !== null) return dark;
  }

  return false;
}

function setCursorColor(dark) {
  document.documentElement.style.setProperty(
    "--cursor-color",
    dark ? "#ffffff" : "#000000"
  );
}

function CustomCursor() {
  const location = useLocation();
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const [isMouseIn, setIsMouseIn] = useState(true);

  useEffect(() => {
    const checkTouchDevice = () => {
      setIsTouchDevice(
        ("ontouchstart" in window &&
          !window.matchMedia("(pointer: fine)").matches) ||
          navigator.maxTouchPoints > 1
      );
    };

    checkTouchDevice();
    window.addEventListener("resize", checkTouchDevice);

    const handleMouseEnter = () => setIsMouseIn(true);
    const handleMouseLeave = () => setIsMouseIn(false);
    window.addEventListener("mouseenter", handleMouseEnter);
    window.addEventListener("mouseleave", handleMouseLeave);

    return () => {
      window.removeEventListener("resize", checkTouchDevice);
      window.removeEventListener("mouseenter", handleMouseEnter);
      window.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, []);

  useEffect(() => {
    if (isTouchDevice) return undefined;

    let frame = 0;
    let lastDark = false;
    setCursorColor(false);

    const onMove = (event) => {
      if (frame) return;
      const { clientX, clientY } = event;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        const dark = isOverDarkSurface(clientX, clientY);
        if (dark !== lastDark) {
          lastDark = dark;
          setCursorColor(dark);
        }
      });
    };

    window.addEventListener("mousemove", onMove, { passive: true });
    return () => {
      window.removeEventListener("mousemove", onMove);
      if (frame) window.cancelAnimationFrame(frame);
      setCursorColor(false);
    };
  }, [isTouchDevice]);

  // Clicking a link leaves the cursor "active" because the element unmounts
  // before mouseout. A synthetic mouseup clears that without remounting at (0,0).
  useEffect(() => {
    window.dispatchEvent(new MouseEvent("mouseup", { bubbles: true }));
  }, [location.pathname]);

  if (isTouchDevice || !isMouseIn) {
    return null;
  }

  return (
    <AnimatedCursor
      innerSize={8}
      outerSize={35}
      innerScale={1}
      outerScale={1.7}
      outerAlpha={0}
      hasBlendMode={false}
      innerStyle={{
        backgroundColor: "var(--cursor-color, #000000)",
      }}
      outerStyle={{
        border: "3px solid var(--cursor-color, #000000)",
      }}
      clickables={[
        "a",
        'input[type="text"]',
        'input[type="email"]',
        'input[type="number"]',
        'input[type="submit"]',
        'input[type="image"]',
        "label[for]",
        "select",
        "textarea",
        "button",
        ".link",
      ]}
    />
  );
}

export default CustomCursor;
